import type { SvgIconTypeMap } from "@mui/material";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CampaignIcon from "@mui/icons-material/Campaign";
import WebIcon from "@mui/icons-material/Web";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import AllInboxOutlinedIcon from "@mui/icons-material/AllInboxOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import PaymentIcon from "@mui/icons-material/Payment";
import UndoIcon from "@mui/icons-material/Undo";
import GavelIcon from "@mui/icons-material/Gavel";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ForumIcon from "@mui/icons-material/Forum";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PaidIcon from "@mui/icons-material/Paid";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import { PagePaths } from "./routes";

export interface MenuItemProps {
  to: string;
  text: string;
  icon: OverridableComponent<SvgIconTypeMap> & {
    muiName: string;
  };
}

export interface MenuSectionProps {
  section: string;
  title: string;
  /** Без сворачиваемого заголовка, пункты видны всегда. */
  alwaysOpen?: boolean;
  items: MenuItemProps[];
}

export const MenuSections: MenuSectionProps[] = [
  {
    section: "dashboard",
    title: "Обзор",
    alwaysOpen: true,
    items: [
      {
        to: PagePaths.admin,
        text: "Панель управления",
        icon: SpaceDashboardOutlinedIcon,
      },
    ],
  },
  {
    section: "users",
    title: "Пользователи",
    items: [
      {
        to: PagePaths.users,
        text: "Пользователи",
        icon: PeopleAltOutlinedIcon,
      },
    ],
  },
  {
    section: "organizations",
    title: "Организации",
    items: [
      {
        to: PagePaths.organizations,
        text: "Организации",
        icon: CampaignIcon,
      },
    ],
  },
  {
    section: "platforms",
    title: "Платформы",
    items: [
      {
        to: PagePaths.platforms,
        text: "Платформы",
        icon: WebIcon,
      },
    ],
  },
  {
    section: "contents",
    title: "Контент",
    items: [
      {
        to: PagePaths.contents,
        text: "Контент",
        icon: VideoLibraryOutlinedIcon,
      },
      {
        to: PagePaths.streams,
        text: "Эфиры",
        icon: LiveTvOutlinedIcon,
      },
      {
        to: PagePaths.streamEntrypoints,
        text: "Точки входа",
        icon: SettingsInputAntennaIcon,
      },
      {
        to: PagePaths.collectionTypes,
        text: "Типы коллекций",
        icon: AllInboxOutlinedIcon,
      },
    ],
  },
  {
    section: "documents",
    title: "Документы",
    items: [
      {
        to: PagePaths.documents,
        text: "Документы",
        icon: DescriptionOutlinedIcon,
      },
    ],
  },
  {
    section: "payments",
    title: "Платежи",
    items: [
      { to: PagePaths.payments, text: "Платежи", icon: PaymentIcon },
      { to: PagePaths.refunds, text: "Возвраты", icon: UndoIcon },
      { to: PagePaths.disputes, text: "Диспуты", icon: GavelIcon },
      {
        to: PagePaths.productPurchases,
        text: "Покупки",
        icon: ShoppingCartIcon,
      },
      {
        to: PagePaths.subscriptions,
        text: "Подписки",
        icon: AutorenewIcon,
      },
    ],
  },
  {
    section: "billing",
    title: "Биллинг",
    items: [
      {
        to: PagePaths.billingContracts,
        text: "Контракты",
        icon: AssignmentOutlinedIcon,
      },
      {
        to: PagePaths.billingPayments,
        text: "Платежи по контрактам",
        icon: ReceiptLongIcon,
      },
      {
        to: PagePaths.billingReports,
        text: "Отчёты доли с продаж",
        icon: HandshakeOutlinedIcon,
      },
      {
        to: PagePaths.billingSeller,
        text: "Реквизиты продавца",
        icon: SettingsOutlinedIcon,
      },
      {
        to: PagePaths.billingExpenses,
        text: "Наши траты",
        icon: PaidOutlinedIcon,
      },
    ],
  },
  {
    section: "assistant",
    title: "Помощник",
    items: [
      {
        to: PagePaths.assistantConversations,
        text: "Диалоги",
        icon: ForumIcon,
      },
      {
        to: PagePaths.assistantRequests,
        text: "Обращения",
        icon: SupportAgentIcon,
      },
      {
        to: PagePaths.assistantPlans,
        text: "Планы",
        icon: ChecklistRoundedIcon,
      },
      {
        to: PagePaths.assistantUsage,
        text: "Расход",
        icon: PaidIcon,
      },
    ],
  },
  {
    section: "system",
    title: "Система",
    items: [
      {
        to: PagePaths.mailerConfigs,
        text: "Мейлеры",
        icon: EmailOutlinedIcon,
      },
      {
        to: PagePaths.configs,
        text: "Конфиги",
        icon: TuneOutlinedIcon,
      },
      {
        to: PagePaths.languages,
        text: "Языки",
        icon: LanguageOutlinedIcon,
      },
      {
        to: PagePaths.translations,
        text: "Переводы",
        icon: TranslateOutlinedIcon,
      },
      {
        to: PagePaths.migrations,
        text: "Миграции",
        icon: SettingsOutlinedIcon,
      },
      {
        to: PagePaths.queues,
        text: "Очереди",
        icon: LayersOutlinedIcon,
      },
    ],
  },
];
